import { getClientLogger } from '@/lib/logger/pino-logger.client';
import {
  IBlogPostData,
  IGetBlogPosts,
  IGetBlogPostsResponse,
  IGetBlogPostsSearch,
  ISearchResults,
} from '@lamesarv-sdk/types';
import { useEffect, useState } from 'react';

export const useGetBlogItems = (props: IGetBlogPosts) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [items, setItems] = useState<ISearchResults<IBlogPostData>>({
    hits: [],
    nbHits: 0,
    nbPages: 0,
  });

  const resetItems = () => {
    setItems({
      hits: [],
      nbHits: 0,
      nbPages: 0,
    });
    setLoading(false);
    setError(false);
  };

  const logger = getClientLogger('hooks.blog.useGetBlogItems');

  const fetchItems = async (fetchProps?: IGetBlogPosts): Promise<ISearchResults<IBlogPostData>> => {
    const _props = fetchProps ?? props;

    try {
      setLoading(true);
      setError(false);

      const response = await fetch('/api/blog/posts', {
        method: 'POST',
        body: JSON.stringify(_props),
      });
      const responseData: IGetBlogPostsResponse = await response.json();

      const { data, total } = responseData;

      const blogData = {
        hits: data.map((item) => ({ document: item })),
        nbHits: total ?? 0,
        nbPages: Math.ceil((total ?? 0) / (_props?.pageSize ?? 1)),
      };

      setItems(blogData);

      if (_props.callback) {
        _props.callback(data);
      }

      setLoading(false);

      return blogData;
    } catch (err) {
      logger.error(`Error fetching blog posts: ${(err as Error).message}`);
      setLoading(false);
      setError(true);
      return {
        hits: [],
        nbHits: 0,
        nbPages: 0,
      };
    }
  };

  useEffect(() => {
    if (!props.skipFetch) {
      fetchItems();
    }
  }, []);

  return {
    loading,
    error,
    items,
    fetchItems,
    resetItems,
  };
};

export const useSearchBlogItems = (props: IGetBlogPostsSearch) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [items, setItems] = useState<ISearchResults<IBlogPostData>>({
    hits: [],
    nbHits: 0,
    nbPages: 0,
  });

  const resetItems = () => {
    setItems({
      hits: [],
      nbHits: 0,
      nbPages: 0,
    });
    setLoading(false);
    setError(false);
  };

  const logger = getClientLogger('hooks.blog.useSearchBlogItems');

  const fetchItems = async (fetchProps?: IGetBlogPostsSearch): Promise<ISearchResults<IBlogPostData>> => {
    const _props = fetchProps ?? props;

    try {
      setLoading(true);
      setError(false);

      const response = await fetch('/api/blog/posts', {
        method: 'POST',
        body: JSON.stringify(_props),
      });
      const responseData: IGetBlogPostsResponse = await response.json();

      const { data, total } = responseData;

      const blogData = {
        hits: data.map((item) => ({ document: item })),
        nbHits: total ?? 0,
        nbPages: Math.ceil((total ?? 0) / (_props?.pageSize ?? 1)),
      };

      setItems(blogData);

      if (_props.callback) {
        _props.callback(data);
      }

      setLoading(false);

      return blogData;
    } catch (err) {
      logger.error(`Error fetching blog posts: ${(err as Error).message}`);
      setLoading(false);
      setError(true);
      return {
        hits: [],
        nbHits: 0,
        nbPages: 0,
      };
    }
  };

  useEffect(() => {
    if (!props.skipFetch) {
      fetchItems();
    }
  }, []);

  return {
    loading,
    error,
    items,
    fetchItems,
    resetItems,
  };
};
